import React, { useState, useEffect } from 'react';
import { Trophy, Plus, CreditCard as Edit2, Trash2, Calendar, Users, Music, Eye, Mail, Filter, Search, X, Check, TriangleAlert as AlertTriangle, Tag, Lock, Clock as Unlock, UserX, Ban, Award, ExternalLink, Palette, Image, GitBranch, ChevronDown, ChevronRight, Mic, Video, Star, Zap, RotateCcw, Sparkles, Image as ImageIcon, Copy } from 'lucide-react';
import { tournamentService, Tournament, TournamentCategory, CreateTournamentData } from '../services/TournamentService';
import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAllRows';
import { BracketGenerationService } from '../services/BracketGenerationService';
import { TournamentCardPreview } from '../components/TournamentCardPreview';
import { SimulateTournament } from '../components/SimulateTournament';
import AdminBracketManager from '../components/AdminBracketManager';
import SponsorLogoCropper from '../components/SponsorLogoCropper';
import ShaderBackground from '../components/ShaderBackground';
import BracketPreviewModal from '../components/BracketPreviewModal';
import { SHADER_PRESETS } from '../lib/shaderPresets';

interface Song {
  id: string;
  title: string;
  artist: string;
  cover_url: string;
}

export const AdminTournaments: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [categories, setCategories] = useState<TournamentCategory[]>([]);
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showSimulateModal, setShowSimulateModal] = useState(false);
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'upcoming' | 'active' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tournamentsData, categoriesData, songsData] = await Promise.all([
        tournamentService.getAllTournaments(),
        tournamentService.getAllCategories(),
        loadSongs()
      ]);
      setTournaments(tournamentsData);
      setCategories(categoriesData);
      setSongs(songsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const loadSongs = async (): Promise<Song[]> => {
    return fetchAllRows<Song>(() =>
      supabase
        .from('songs')
        .select('id, title, artist, cover_url')
        .order('title')
    );
  };

  const handleDeleteTournament = async () => {
    if (!selectedTournament) return;

    try {
      await tournamentService.deleteTournament(selectedTournament.id);
      setSuccess('Tournament deleted successfully');
      setShowDeleteModal(false);
      setSelectedTournament(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting tournament:', err);
      setError('Failed to delete tournament');
    }
  };

  const handleDuplicateTournament = async (name: string) => {
    if (!selectedTournament) return;
    try {
      setDuplicating(true);
      await tournamentService.duplicateTournament(selectedTournament.id, { title: name });
      setSuccess('Tournament duplicated successfully');
      setShowDuplicateModal(false);
      setSelectedTournament(null);
      await loadData();
    } catch (err) {
      console.error('Error duplicating tournament:', err);
      setError('Failed to duplicate tournament');
    } finally {
      setDuplicating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleToggleRegistrationLock = async (tournament: Tournament) => {
    try {
      if (tournament.registration_locked) {
        const { error } = await supabase
          .from('tournaments')
          .update({
            registration_locked: false,
            locked_participant_count: null,
            locked_at: null,
            status: 'upcoming'
          })
          .eq('id', tournament.id);

        if (error) throw error;
        setSuccess('Registration unlocked and tournament reverted to upcoming');
      } else {
        const { count, error: countError } = await supabase
          .from('tournament_entries')
          .select('*', { count: 'exact', head: true })
          .eq('tournament_id', tournament.id);

        if (countError) throw countError;

        const participantCount = count || 0;
        const bracketSize = getNextPowerOfTwo(participantCount);

        const { error } = await supabase
          .from('tournaments')
          .update({
            registration_locked: true,
            locked_participant_count: bracketSize,
            locked_at: new Date().toISOString(),
            status: 'active'
          })
          .eq('id', tournament.id);

        if (error) throw error;
        setSuccess(`Registration locked with ${participantCount} participants (bracket size: ${bracketSize}). Tournament is now active.`);
      }

      await loadData();
    } catch (err) {
      console.error('Error toggling registration lock:', err);
      setError('Failed to toggle registration lock');
    }
  };

  const getNextPowerOfTwo = (n: number): number => {
    if (n <= 0) return 2;
    let power = 1;
    while (power < n) {
      power *= 2;
    }
    return power;
  };

  const getActualStatus = (tournament: Tournament): 'upcoming' | 'active' | 'completed' => {
    const now = new Date();
    const end = new Date(tournament.end_date);
    if (now > end) return 'completed';
    if (tournament.status === 'active' || tournament.registration_locked) return 'active';
    const start = new Date(tournament.start_date);
    if (now >= start) return 'active';
    return 'upcoming';
  };

  const isRegistrationClosed = (tournament: Tournament): boolean => {
    if (tournament.registration_locked) return true;
    if (tournament.registration_close_date) {
      return new Date(tournament.registration_close_date) < new Date();
    }
    return false;
  };

  const handleGenerateBracket = async (tournament: Tournament) => {
    try {
      setSuccess(null);
      setError(null);

      const { count, error: countError } = await supabase
        .from('tournament_entries')
        .select('*', { count: 'exact', head: true })
        .eq('tournament_id', tournament.id)
        .in('status', ['registered', 'evaluated']);

      if (countError) throw countError;

      const participantCount = count || 0;
      if (participantCount < 2) {
        setError('Need at least 2 participants to generate a bracket');
        return;
      }

      const { data: existingBracket } = await supabase
        .from('bracket_progression')
        .select('id')
        .eq('tournament_id', tournament.id)
        .maybeSingle();

      if (existingBracket) {
        await BracketGenerationService.regenerateBracket(tournament.id, 'random', true);
        setSuccess(`Bracket regenerated with ${participantCount} participants. Registration locked and tournament activated.`);
      } else {
        await BracketGenerationService.generateBracket(tournament.id, 'random', true);
        setSuccess(`Bracket generated with ${participantCount} participants. Registration locked and tournament activated.`);
      }

      await loadData();
    } catch (err) {
      console.error('Error generating bracket:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate bracket');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-900/30 text-green-400">
            Active
          </span>
        );
      case 'upcoming':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-900/30 text-blue-400">
            Upcoming
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-900/30 text-gray-400">
            Completed
          </span>
        );
      default:
        return null;
    }
  };

  const filteredTournaments = tournaments.filter(tournament => {
    const actualStatus = getActualStatus(tournament);
    const matchesStatus = filterStatus === 'all' || actualStatus === filterStatus;
    const matchesSearch = tournament.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          tournament.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Tournament Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus size={18} className="mr-2" />
          Create Tournament
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 flex items-center justify-between text-red-500">
          <div className="flex items-center">
            <AlertTriangle size={20} className="mr-2" />
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 flex items-center justify-between text-green-500">
          <div className="flex items-center">
            <Check size={20} className="mr-2" />
            <p>{success}</p>
          </div>
          <button onClick={() => setSuccess(null)}>
            <X size={18} />
          </button>
        </div>
      )}

      <div className="card mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            <input
              type="text"
              placeholder="Search tournaments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-kod-accent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'all'
                  ? 'bg-kod-accent text-white'
                  : 'bg-kod-purple-700 text-white/70 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('upcoming')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'upcoming'
                  ? 'bg-kod-accent text-white'
                  : 'bg-kod-purple-700 text-white/70 hover:text-white'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'active'
                  ? 'bg-kod-accent text-white'
                  : 'bg-kod-purple-700 text-white/70 hover:text-white'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterStatus('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === 'completed'
                  ? 'bg-kod-accent text-white'
                  : 'bg-kod-purple-700 text-white/70 hover:text-white'
              }`}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {filteredTournaments.length === 0 ? (
        <div className="card text-center py-12">
          <div className="inline-block bg-kod-purple-700/50 p-4 rounded-full mb-4">
            <Trophy size={36} className="text-white/50" />
          </div>
          <h2 className="text-xl font-bold mb-2">No Tournaments Found</h2>
          <p className="text-white/70 mb-6">
            {searchQuery || filterStatus !== 'all'
              ? 'Try adjusting your filters or search query'
              : 'Create your first tournament to get started'}
          </p>
          {!searchQuery && filterStatus === 'all' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <Plus size={18} className="mr-2" />
              Create Tournament
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredTournaments.map((tournament) => (
            <div key={tournament.id} className="card hover:bg-kod-purple-700/50 transition-colors">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getStatusBadge(getActualStatus(tournament))}
                    {tournament.is_private && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-900/30 text-amber-400">
                        <Lock size={12} className="mr-1" />
                        Private
                      </span>
                    )}
                    {tournament.category && (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold"
                        style={{
                          backgroundColor: `${tournament.category.color}20`,
                          color: tournament.category.color
                        }}
                      >
                        <Tag size={12} className="mr-1" />
                        {tournament.category.name}
                      </span>
                    )}
                    {tournament.registration_locked && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-900/30 text-red-400">
                        <Lock size={12} className="mr-1" />
                        Locked
                      </span>
                    )}
                    {tournament.is_simulated && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-900/30 text-orange-400">
                        <Zap size={12} className="mr-1" />
                        Simulated
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{tournament.title}</h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">{tournament.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <div className="text-white/60 flex items-center mb-1">
                    <Calendar size={14} className="mr-1" />
                    Timeline
                  </div>
                  <div>{formatDate(tournament.start_date)} - {formatDate(tournament.end_date)}</div>
                </div>
                <div>
                  <div className="text-white/60 flex items-center mb-1">
                    <Lock size={14} className="mr-1" />
                    Registration Closes
                  </div>
                  <div>
                    {tournament.registration_close_date
                      ? formatDate(tournament.registration_close_date)
                      : tournament.registration_locked
                        ? 'Manually Locked'
                        : 'Not Set'}
                  </div>
                </div>
                <div>
                  <div className="text-white/60 flex items-center mb-1">
                    <Users size={14} className="mr-1" />
                    Max Participants
                  </div>
                  <div>{tournament.max_participants || 'Unlimited'}</div>
                </div>
                <div>
                  <div className="text-white/60 flex items-center mb-1">
                    <Music size={14} className="mr-1" />
                    Songs
                  </div>
                  <div>{tournament.songs?.length || 0} songs</div>
                </div>
                <div>
                  <div className="text-white/60 flex items-center mb-1">
                    <Trophy size={14} className="mr-1" />
                    Prize
                  </div>
                  <div className="line-clamp-1">
                    {tournament.prize_pool_amount
                      ? `${tournament.prize_pool_currency || '$'}${tournament.prize_pool_amount}`
                      : 'Other'}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setSelectedTournament(tournament);
                    setShowParticipantsModal(true);
                  }}
                  className="flex-1 btn-outline flex items-center justify-center"
                >
                  <Users size={16} className="mr-2" />
                  Participants
                </button>
                <button
                  onClick={() => {
                    setSelectedTournament(tournament);
                    setShowEditModal(true);
                  }}
                  className="flex-1 btn-outline flex items-center justify-center"
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit
                </button>
                {tournament.is_private && (
                  <button
                    onClick={() => {
                      setSelectedTournament(tournament);
                      setShowInviteModal(true);
                    }}
                    className="flex-1 btn-outline flex items-center justify-center"
                  >
                    <Mail size={16} className="mr-2" />
                    Invites
                  </button>
                )}
                {isRegistrationClosed(tournament) && (
                  <button
                    onClick={() => handleGenerateBracket(tournament)}
                    className="btn-outline flex items-center justify-center px-4 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50"
                    title="Generate or regenerate tournament bracket"
                  >
                    <GitBranch size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleToggleRegistrationLock(tournament)}
                  className={`btn-outline flex items-center justify-center px-4 ${
                    tournament.registration_locked
                      ? 'text-green-400 hover:bg-green-500/10 hover:border-green-500/50'
                      : 'text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-500/50'
                  }`}
                  title={tournament.registration_locked ? 'Unlock registration' : 'Lock registration'}
                >
                  {tournament.registration_locked ? <Unlock size={16} /> : <Lock size={16} />}
                </button>
                {tournament.is_simulated && (
                  <button
                    onClick={() => {
                      setSelectedTournament(tournament);
                      setShowSimulateModal(true);
                    }}
                    className="btn-outline text-orange-400 hover:bg-orange-500/10 hover:border-orange-500/50 flex items-center justify-center gap-1.5 px-3"
                    title="Open simulation manager"
                  >
                    <Zap size={16} />
                    <span className="text-sm">Simulate</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedTournament(tournament);
                    setShowBracketModal(true);
                  }}
                  className="btn-outline text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50 flex items-center justify-center gap-1.5 px-3"
                  title="View tournament bracket"
                >
                  <Trophy size={16} />
                  <span className="text-sm">Bracket</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedTournament(tournament);
                    setShowDuplicateModal(true);
                  }}
                  className="btn-outline text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/50 flex items-center justify-center gap-1.5 px-3"
                  title="Duplicate tournament"
                >
                  <Copy size={16} />
                  <span className="text-sm">Duplicate</span>
                </button>
                <button
                  onClick={() => {
                    setSelectedTournament(tournament);
                    setShowDeleteModal(true);
                  }}
                  className="btn-outline text-red-400 hover:bg-red-500/10 hover:border-red-500/50 flex items-center justify-center px-4"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <TournamentFormModal
          categories={categories}
          songs={songs}
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            try {
              await tournamentService.createTournament(data);
              setSuccess('Tournament created successfully');
              setShowCreateModal(false);
              await loadData();
            } catch (err) {
              console.error('Error creating tournament:', err);
              setError('Failed to create tournament');
            }
          }}
        />
      )}

      {showEditModal && selectedTournament && (
        <TournamentFormModal
          tournament={selectedTournament}
          categories={categories}
          songs={songs}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTournament(null);
          }}
          onSave={async (data) => {
            try {
              await tournamentService.updateTournament(selectedTournament.id, data);
              setSuccess('Tournament updated successfully');
              setShowEditModal(false);
              setSelectedTournament(null);
              await loadData();
            } catch (err) {
              console.error('Error updating tournament:', err);
              setError('Failed to update tournament');
            }
          }}
        />
      )}

      {showDeleteModal && selectedTournament && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-red-500/20 p-3 rounded-full mr-4">
                <AlertTriangle size={24} className="text-red-400" />
              </div>
              <h3 className="text-xl font-bold">Delete Tournament</h3>
            </div>
            <p className="text-white/70 mb-6">
              Are you sure you want to delete "{selectedTournament.title}"? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTournament(null);
                }}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTournament}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && selectedTournament && (
        <DuplicateModal
          title={selectedTournament.title}
          label="Tournament Name"
          loading={duplicating}
          onClose={() => { setShowDuplicateModal(false); setSelectedTournament(null); }}
          onConfirm={handleDuplicateTournament}
        />
      )}

      {showInviteModal && selectedTournament && (
        <InviteModal
          tournament={selectedTournament}
          onClose={() => {
            setShowInviteModal(false);
            setSelectedTournament(null);
          }}
        />
      )}

      {showParticipantsModal && selectedTournament && (
        <ParticipantsModal
          tournament={selectedTournament}
          onClose={() => {
            setShowParticipantsModal(false);
            setSelectedTournament(null);
          }}
          onUpdate={() => loadData()}
        />
      )}

      {showSimulateModal && selectedTournament && (
        <SimulateTournament
          tournamentId={selectedTournament.id}
          tournamentTitle={selectedTournament.title}
          tournamentSongs={(selectedTournament as any).songs || []}
          maxParticipants={(selectedTournament as any).max_participants || 8}
          onClose={() => {
            setShowSimulateModal(false);
            setSelectedTournament(null);
            loadData();
          }}
        />
      )}
      {showBracketModal && selectedTournament && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-white/10 w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
              <div className="flex items-center gap-3">
                <Trophy size={20} className="text-cyan-400" />
                <span className="text-white font-semibold">{selectedTournament.title} — Bracket</span>
              </div>
              <button
                onClick={() => {
                  setShowBracketModal(false);
                  setSelectedTournament(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <AdminBracketManager
                tournamentId={selectedTournament.id}
                onClose={() => {
                  setShowBracketModal(false);
                  setSelectedTournament(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DuplicateModalProps {
  title: string;
  label: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
}

const DuplicateModal: React.FC<DuplicateModalProps> = ({ title, label, loading, onClose, onConfirm }) => {
  const [name, setName] = useState(`Copy of ${title}`);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-2">Duplicate Tournament</h3>
        <p className="text-sm text-white/60 mb-4">
          This will create a new tournament with the same settings, branding, and song list. Participants and scores will not be copied.
        </p>
        <label className="block text-sm font-medium text-white/70 mb-1.5">{label}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:border-sky-400 focus:outline-none"
          autoFocus
        />
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-slate-600 text-white rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(name)}
            disabled={loading || !name.trim()}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-lg font-medium hover:shadow-lg hover:shadow-sky-500/25 transition disabled:opacity-50"
          >
            {loading ? 'Duplicating...' : 'Duplicate'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface TournamentFormModalProps {
  tournament?: Tournament;
  categories: TournamentCategory[];
  songs: Song[];
  onClose: () => void;
  onSave: (data: CreateTournamentData) => Promise<void>;
}

const TournamentFormModal: React.FC<TournamentFormModalProps> = ({
  tournament,
  categories,
  songs,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<CreateTournamentData>({
    title: tournament?.title || '',
    description: tournament?.description || '',
    start_date: tournament?.start_date?.split('T')[0] || '',
    end_date: tournament?.end_date?.split('T')[0] || '',
    registration_close_date: tournament?.registration_close_date?.split('T')[0] || '',
    entry_fee: tournament?.entry_fee || undefined,
    prize_pool_amount: tournament?.prize_pool_amount || undefined,
    prize_pool_currency: tournament?.prize_pool_currency || '$',
    prize_description: tournament?.prize_description || '',
    prize_display: tournament?.prize_display || undefined,
    title_sponsor: tournament?.title_sponsor || '',
    max_participants: tournament?.max_participants as any || undefined,
    category_id: tournament?.category_id || undefined,
    is_private: tournament?.is_private || false,
    is_shareable: tournament?.is_shareable || false,
    allow_pre_registration: tournament?.allow_pre_registration ?? true,
    is_featured: tournament?.is_featured || false,
    is_simulated: tournament?.is_simulated || false,
    primary_color: tournament?.primary_color || undefined,
    secondary_color: tournament?.secondary_color || undefined,
    logo_url: tournament?.logo_url || undefined,
    background_image_url: tournament?.background_image_url || undefined,
    background_type: tournament?.background_type || 'shader',
    shader_preset: tournament?.shader_preset || 'hex_grid',
    sponsor_logos: tournament?.sponsor_logos || [],
    sponsor_links: tournament?.sponsor_links || [],
    rules: tournament?.rules || [
      'Choose from the available songs and record your performance',
      'Only one submission per participant is allowed',
      'Your performance will be scored based on pitch accuracy, timing, and overall quality',
      'The top 64 performers will advance to the bracket stage',
      'Winners will be announced at the end of the tournament period',
      'Prize distribution will be handled within 7 days of tournament completion',
      'All recordings must comply with platform guidelines'
    ],
    round_duration_minutes: tournament?.round_duration_minutes || 2880,
    song_ids: tournament?.songs?.map(s => s.song_id) || []
  });
  const [saving, setSaving] = useState(false);
  const [songSearch, setSongSearch] = useState('');
  const [logoUploadMode, setLogoUploadMode] = useState<'url' | 'upload'>(tournament?.logo_url ? 'url' : 'upload');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [bgImageMode, setBgImageMode] = useState<'shader' | 'preset' | 'upload'>(tournament?.background_type === 'image' ? 'upload' : tournament?.background_type === 'gradient' ? 'preset' : 'shader');
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgUploadError, setBgUploadError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showBracketPreview, setShowBracketPreview] = useState(false);
  const [showSponsorCropper, setShowSponsorCropper] = useState(false);
  const [uploadingSponsor, setUploadingSponsor] = useState(false);

  const BG_PRESETS = [
    {
      label: 'Stage Lights',
      url: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
    },
    {
      label: 'Concert Crowd',
      url: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?auto=format&fit=crop&w=800&q=80'
    },
    {
      label: 'Microphone',
      url: 'https://images.unsplash.com/photo-1516280440614-37939bbacd81?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
    },
    {
      label: 'Music Notes',
      url: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
    },
    {
      label: 'Trophy',
      url: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
    },
    {
      label: 'Spotlight',
      url: 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?auto=format&fit=crop&w=800&q=80'
    }
  ];

  const handleLogoUpload = async (file: File) => {
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoUploadError('Please upload a valid image file (PNG, JPG, GIF, WebP, or SVG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setLogoUploadError('File size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);
      setLogoUploadError(null);

      const fileExt = file.name.split('.').pop();
      const fileName = `logos/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('tournament_assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('tournament_assets')
        .getPublicUrl(fileName);

      setFormData({ ...formData, logo_url: publicUrl });
    } catch (err) {
      console.error('Error uploading logo:', err);
      setLogoUploadError('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBgImageUpload = async (file: File) => {
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setBgUploadError('Please upload a PNG, JPG, or WebP image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setBgUploadError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingBg(true);
      setBgUploadError(null);

      const fileExt = file.name.split('.').pop();
      const fileName = `backgrounds/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('tournament_assets')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('tournament_assets')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, background_image_url: publicUrl }));
    } catch (err) {
      console.error('Error uploading background image:', err);
      setBgUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploadingBg(false);
    }
  };

  const handleSponsorCropComplete = async (blob: Blob) => {
    if ((formData.sponsor_logos?.length || 0) >= 5) return;
    try {
      setUploadingSponsor(true);
      setShowSponsorCropper(false);
      const fileName = `sponsors/${Date.now()}-${Math.random().toString(36).substring(7)}.png`;
      const { error: uploadError } = await supabase.storage
        .from('tournament_assets')
        .upload(fileName, blob, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('tournament_assets').getPublicUrl(fileName);
      setFormData(prev => ({
        ...prev,
        sponsor_logos: [...(prev.sponsor_logos || []), publicUrl],
        sponsor_links: [...(prev.sponsor_links || []), ''],
      }));
    } catch (err) {
      console.error('Error uploading sponsor logo:', err);
    } finally {
      setUploadingSponsor(false);
    }
  };

  const removeSponsorLogo = (index: number) => {
    setFormData(prev => ({
      ...prev,
      sponsor_logos: (prev.sponsor_logos || []).filter((_, i) => i !== index),
      sponsor_links: (prev.sponsor_links || []).filter((_, i) => i !== index),
    }));
  };

  const updateSponsorLink = (index: number, link: string) => {
    setFormData(prev => {
      const links = [...(prev.sponsor_links || [])];
      while (links.length <= index) links.push('');
      links[index] = link;
      return { ...prev, sponsor_links: links };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSave = { ...formData };
      if (dataToSave.registration_close_date && !dataToSave.registration_close_date.includes('T')) {
        dataToSave.registration_close_date = `${dataToSave.registration_close_date}T23:59:59.999Z`;
      }
      // Sync background_type with bgImageMode
      dataToSave.background_type = bgImageMode === 'shader' ? 'shader' : bgImageMode === 'upload' ? 'image' : 'gradient';
      await onSave(dataToSave);
    } finally {
      setSaving(false);
    }
  };

  const filteredSongs = songs.filter(song =>
    song.title.toLowerCase().includes(songSearch.toLowerCase()) ||
    song.artist.toLowerCase().includes(songSearch.toLowerCase())
  );

  const toggleSong = (songId: string) => {
    setFormData(prev => ({
      ...prev,
      song_ids: prev.song_ids.includes(songId)
        ? prev.song_ids.filter(id => id !== songId)
        : [...prev.song_ids, songId]
    }));
  };

  return (
    <>
    {showSponsorCropper && (
      <SponsorLogoCropper
        onCropComplete={handleSponsorCropComplete}
        onCancel={() => setShowSponsorCropper(false)}
      />
    )}
    {showBracketPreview && (
      <BracketPreviewModal
        title={formData.title || 'Tournament Bracket'}
        primaryColor={formData.primary_color || '#f97316'}
        secondaryColor={formData.secondary_color || '#3b82f6'}
        backgroundType={bgImageMode === 'shader' ? 'shader' : bgImageMode === 'upload' ? 'image' : 'gradient'}
        shaderPreset={formData.shader_preset || 'hex_grid'}
        backgroundImageUrl={formData.background_image_url}
        prizeDisplay={formData.prize_display}
        titleSponsor={formData.title_sponsor}
        logoUrl={formData.logo_url}
        sponsorLogos={formData.sponsor_logos}
        sponsorLinks={formData.sponsor_links}
        onClose={() => setShowBracketPreview(false)}
      />
    )}
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-kod-purple-800 rounded-lg max-w-4xl w-full my-8">
        <div className="p-6 border-b border-kod-purple-600">
          <h3 className="text-xl font-bold">
            {tournament ? 'Edit Tournament' : 'Create Tournament'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Registration Close Date
                <span className="text-white/50 font-normal ml-2">(Last day to register)</span>
              </label>
              <input
                type="date"
                value={formData.registration_close_date || ''}
                onChange={(e) => setFormData({ ...formData, registration_close_date: e.target.value || undefined })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
              <p className="text-xs text-white/50 mt-1">
                Registration closes at end of this date (UTC). Before this date, the bracket preview updates with each new registration. Generate the bracket to lock registration and activate the tournament.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Round Duration (minutes)
                <span className="text-white/50 font-normal ml-2">(Time per round for submissions)</span>
              </label>
              <div className="flex gap-2 mb-2">
                {[
                  { label: '1 hour', value: 60 },
                  { label: '12 hours', value: 720 },
                  { label: '1 day', value: 1440 },
                  { label: '2 days', value: 2880 },
                  { label: '5 days', value: 7200 },
                  { label: '7 days', value: 10080 },
                ].map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, round_duration_minutes: preset.value })}
                    className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      formData.round_duration_minutes === preset.value
                        ? 'bg-kod-accent/20 border-kod-accent text-kod-accent'
                        : 'bg-kod-purple-700 border-kod-purple-600 text-white/60 hover:border-kod-accent/50 hover:text-white/80'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="1"
                max="43200"
                value={formData.round_duration_minutes || 2880}
                onChange={(e) => setFormData({ ...formData, round_duration_minutes: e.target.value ? parseInt(e.target.value) : 2880 })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
              <p className="text-xs text-white/50 mt-1">
                How many minutes each participant has to submit their recording per round. Participants who miss the deadline are automatically eliminated.
                {(formData.round_duration_minutes || 2880) >= 60 && (
                  <span className="text-white/40 ml-1">
                    ({Math.floor((formData.round_duration_minutes || 2880) / 1440)}d {Math.floor(((formData.round_duration_minutes || 2880) % 1440) / 60)}h {(formData.round_duration_minutes || 2880) % 60}m)
                  </span>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category_id || ''}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value || undefined })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              >
                <option value="">No Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Max Participants</label>
              <select
                value={formData.max_participants || ''}
                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value ? parseInt(e.target.value) as any : undefined })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              >
                <option value="">Unlimited / TBD</option>
                <option value="4">4 participants</option>
                <option value="8">8 participants</option>
                <option value="16">16 participants</option>
                <option value="32">32 participants</option>
                <option value="64">64 participants</option>
              </select>
              <p className="text-xs text-white/40 mt-1">Must be a power of 2 for clean bracket generation</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Entry Fee (coins)</label>
              <input
                type="number"
                min="0"
                value={formData.entry_fee || ''}
                onChange={(e) => setFormData({ ...formData, entry_fee: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                placeholder="Free"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Prize Amount</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.prize_pool_currency || '$'}
                  onChange={(e) => setFormData({ ...formData, prize_pool_currency: e.target.value })}
                  className="w-16 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                />
                <input
                  type="number"
                  min="0"
                  value={formData.prize_pool_amount || ''}
                  onChange={(e) => setFormData({ ...formData, prize_pool_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="flex-1 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Prize Description</label>
              <input
                type="text"
                required
                value={formData.prize_description}
                onChange={(e) => setFormData({ ...formData, prize_description: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                placeholder="e.g., Premium subscription + $500 cash prize"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Prize Display <span className="text-white/40 font-normal">(short callout shown on bracket — e.g. "$500 Cash")</span>
              </label>
              <input
                type="text"
                value={formData.prize_display || ''}
                onChange={(e) => setFormData({ ...formData, prize_display: e.target.value || undefined })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                placeholder="e.g., $500 Cash Prize"
                maxLength={40}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Title Sponsor <span className="text-white/40 font-normal">(shown as "Powered by ..." under the tournament title)</span>
              </label>
              <input
                type="text"
                value={formData.title_sponsor || ''}
                onChange={(e) => setFormData({ ...formData, title_sponsor: e.target.value })}
                className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                placeholder="e.g., ADNOC"
                maxLength={60}
              />
            </div>

            <div className="md:col-span-2 space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_private}
                  onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Private Tournament (invitation only)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.allow_pre_registration}
                  onChange={(e) => setFormData({ ...formData, allow_pre_registration: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Allow Pre-Registration (users can join before start date)</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Featured Tournament (highlight on main page)</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_simulated || false}
                  onChange={(e) => setFormData({ ...formData, is_simulated: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium">
                  Simulated Tournament
                  <span className="text-white/50 font-normal ml-2">(Admin testing — seeds fake participants and generates random scores)</span>
                </span>
              </label>
            </div>

            {formData.is_private && (
              <div className="md:col-span-2 bg-kod-purple-900/50 rounded-lg p-4 border border-kod-purple-600">
                <div className="flex items-center gap-2 mb-4">
                  <Palette size={18} className="text-kod-accent" />
                  <h4 className="font-semibold">Tournament Branding</h4>
                  <span className="text-xs text-white/50">(Private tournaments only)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primary_color || '#f97316'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-10 rounded border border-kod-purple-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color || ''}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        placeholder="#f97316"
                        className="flex-1 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.secondary_color || '#3b82f6'}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-12 h-10 rounded border border-kod-purple-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color || ''}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        placeholder="#3b82f6"
                        className="flex-1 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      <Image size={14} className="inline mr-1" />
                      Logo
                    </label>

                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setLogoUploadMode('upload')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          logoUploadMode === 'upload'
                            ? 'bg-kod-accent text-white'
                            : 'bg-kod-purple-700 text-white/70 hover:text-white'
                        }`}
                      >
                        Upload File
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogoUploadMode('url')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          logoUploadMode === 'url'
                            ? 'bg-kod-accent text-white'
                            : 'bg-kod-purple-700 text-white/70 hover:text-white'
                        }`}
                      >
                        Enter URL
                      </button>
                    </div>

                    {logoUploadMode === 'upload' ? (
                      <div>
                        <div
                          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                            uploadingLogo
                              ? 'border-kod-accent bg-kod-accent/10'
                              : 'border-kod-purple-600 hover:border-kod-purple-500'
                          }`}
                        >
                          {uploadingLogo ? (
                            <div className="flex flex-col items-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent mb-2"></div>
                              <p className="text-sm text-white/70">Uploading...</p>
                            </div>
                          ) : (
                            <>
                              <Image size={32} className="mx-auto text-white/40 mb-2" />
                              <p className="text-sm text-white/70 mb-2">
                                Click to upload a logo
                              </p>
                              <p className="text-xs text-white/50">
                                PNG, JPG, GIF, WebP, or SVG (max 5MB)
                              </p>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleLogoUpload(file);
                                }}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                            </>
                          )}
                        </div>
                        {logoUploadError && (
                          <p className="text-red-400 text-sm mt-2">{logoUploadError}</p>
                        )}
                      </div>
                    ) : (
                      <input
                        type="url"
                        value={formData.logo_url || ''}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value || undefined })}
                        placeholder="https://example.com/logo.png"
                        className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                      />
                    )}

                    {formData.logo_url && (
                      <div className="mt-3 p-3 bg-kod-purple-800 rounded-lg flex items-center justify-between">
                        <img
                          src={formData.logo_url}
                          alt="Logo preview"
                          className="h-16 w-auto object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, logo_url: undefined })}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Remove logo"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="md:col-span-2 bg-kod-purple-900/50 rounded-lg p-4 border border-kod-purple-600">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Image size={18} className="text-kod-accent" />
                  <h4 className="font-semibold">Bracket Background</h4>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg text-sm transition-colors"
                  >
                    <Eye size={14} />
                    {showPreview ? 'Hide Card' : 'Preview Card'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBracketPreview(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg text-sm transition-colors"
                  >
                    <GitBranch size={14} />
                    Preview Bracket
                  </button>
                </div>
              </div>

              {showPreview && (
                <div className="mb-5">
                  <p className="text-xs text-white/50 mb-2">Card preview</p>
                  <div className="max-w-sm">
                    <TournamentCardPreview
                      title={formData.title || 'Tournament Title'}
                      description={formData.description || 'Tournament description appears here.'}
                      startDate={formData.start_date}
                      endDate={formData.end_date}
                      status="active"
                      prizeDescription={formData.prize_description}
                      prizeAmount={formData.prize_pool_amount}
                      priceCurrency={formData.prize_pool_currency}
                      isPrivate={formData.is_private}
                      isFeatured={formData.is_featured}
                      backgroundImageUrl={formData.background_image_url}
                    />
                  </div>
                </div>
              )}

              {/* Background type tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setBgImageMode('shader')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    bgImageMode === 'shader'
                      ? 'bg-kod-accent text-white'
                      : 'bg-kod-purple-700 text-white/70 hover:text-white'
                  }`}
                >
                  <Sparkles size={14} />
                  Shader
                </button>
                <button
                  type="button"
                  onClick={() => setBgImageMode('preset')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    bgImageMode === 'preset'
                      ? 'bg-kod-accent text-white'
                      : 'bg-kod-purple-700 text-white/70 hover:text-white'
                  }`}
                >
                  <ImageIcon size={14} />
                  Photo Preset
                </button>
                <button
                  type="button"
                  onClick={() => setBgImageMode('upload')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    bgImageMode === 'upload'
                      ? 'bg-kod-accent text-white'
                      : 'bg-kod-purple-700 text-white/70 hover:text-white'
                  }`}
                >
                  <Image size={14} />
                  Upload
                </button>
              </div>

              {/* Shader preset picker */}
              {bgImageMode === 'shader' && (
                <div className="space-y-3">
                  <p className="text-xs text-white/50">Animated GLSL shader — tinted to your brand colors</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {SHADER_PRESETS.map(sp => (
                      <button
                        key={sp.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, shader_preset: sp.id }))}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all h-20 flex flex-col items-center justify-center gap-1 ${
                          formData.shader_preset === sp.id
                            ? 'border-kod-accent ring-2 ring-kod-accent/30'
                            : 'border-transparent hover:border-white/30'
                        }`}
                      >
                        {/* Mini live shader preview */}
                        <div className="absolute inset-0">
                          <ShaderBackground
                            preset={sp.id}
                            primaryColor={formData.primary_color || '#f97316'}
                            secondaryColor={formData.secondary_color || '#3b82f6'}
                          />
                        </div>
                        <div className="absolute inset-0 bg-black/30" />
                        <span className="relative z-10 text-xs font-semibold text-white drop-shadow-lg">{sp.name}</span>
                        <span className="relative z-10 text-xs text-white/70 drop-shadow text-center px-1 leading-tight">{sp.description}</span>
                        {formData.shader_preset === sp.id && (
                          <div className="absolute top-1.5 right-1.5 bg-kod-accent rounded-full p-0.5 z-20">
                            <Check size={10} className="text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {bgImageMode === 'preset' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {BG_PRESETS.map(preset => (
                    <button
                      key={preset.url}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, background_image_url: preset.url }))}
                      className={`relative rounded-lg overflow-hidden aspect-video group border-2 transition-all ${
                        formData.background_image_url === preset.url
                          ? 'border-kod-accent ring-2 ring-kod-accent/30'
                          : 'border-transparent hover:border-white/30'
                      }`}
                    >
                      <img
                        src={preset.url}
                        alt={preset.label}
                        className="w-full h-full object-cover"
                      />
                      <div className={`absolute inset-0 transition-opacity ${
                        formData.background_image_url === preset.url
                          ? 'bg-kod-accent/20'
                          : 'bg-black/40 group-hover:bg-black/20'
                      }`} />
                      <span className="absolute bottom-1.5 left-0 right-0 text-center text-xs font-medium text-white drop-shadow">
                        {preset.label}
                      </span>
                      {formData.background_image_url === preset.url && (
                        <div className="absolute top-1.5 right-1.5 bg-kod-accent rounded-full p-0.5">
                          <Check size={10} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, background_image_url: undefined }))}
                    className={`relative rounded-lg overflow-hidden aspect-video border-2 transition-all flex flex-col items-center justify-center gap-1 ${
                      !formData.background_image_url
                        ? 'border-kod-accent ring-2 ring-kod-accent/30 bg-kod-purple-800'
                        : 'border-dashed border-kod-purple-600 hover:border-white/30 bg-kod-purple-800/50'
                    }`}
                  >
                    <X size={18} className="text-white/50" />
                    <span className="text-xs text-white/50">Default</span>
                  </button>
                </div>
              )}

              {bgImageMode === 'upload' && (
                <div>
                  <div
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                      uploadingBg
                        ? 'border-kod-accent bg-kod-accent/10'
                        : 'border-kod-purple-600 hover:border-kod-purple-500'
                    }`}
                  >
                    {uploadingBg ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kod-accent mb-2"></div>
                        <p className="text-sm text-white/70">Uploading...</p>
                      </div>
                    ) : (
                      <>
                        <Image size={32} className="mx-auto text-white/40 mb-2" />
                        <p className="text-sm text-white/70 mb-1">
                          Click to upload a background image
                        </p>
                        <p className="text-xs text-white/40">PNG, JPG, or WebP · Recommended 1200×600 · Max 10MB</p>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleBgImageUpload(file);
                          }}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                      </>
                    )}
                  </div>
                  {bgUploadError && (
                    <p className="text-red-400 text-sm mt-2">{bgUploadError}</p>
                  )}
                </div>
              )}

              {formData.background_image_url && bgImageMode === 'upload' && (
                <div className="mt-3 relative rounded-lg overflow-hidden aspect-video max-w-xs">
                  <img
                    src={formData.background_image_url}
                    alt="Background preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, background_image_url: undefined }))}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-red-500/80 p-1.5 rounded-lg transition-colors"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              )}
            </div>

            {/* Sponsor Logos */}
            <div className="md:col-span-2 bg-kod-purple-900/50 rounded-lg p-4 border border-kod-purple-600">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Award size={18} className="text-kod-accent" />
                  <h4 className="font-semibold">Sponsor Logos</h4>
                  <span className="text-xs text-white/40">({(formData.sponsor_logos || []).length}/5)</span>
                </div>
                {(formData.sponsor_logos || []).length < 5 && (
                  <button
                    type="button"
                    onClick={() => setShowSponsorCropper(true)}
                    disabled={uploadingSponsor}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-kod-accent hover:bg-kod-accent-dark rounded-lg text-sm text-white transition-colors disabled:opacity-50"
                  >
                    {uploadingSponsor ? (
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Plus size={14} />
                    )}
                    Add Logo
                  </button>
                )}
              </div>
              <p className="text-xs text-white/40 mb-3">Logos appear in the sponsor bar below the tournament bracket. Upload up to 5.</p>
              {(formData.sponsor_logos || []).length === 0 ? (
                <p className="text-sm text-white/30 italic">No sponsor logos added yet</p>
              ) : (
                <div className="space-y-3">
                  {(formData.sponsor_logos || []).map((url, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="relative group rounded-lg overflow-hidden border border-kod-purple-600 bg-black flex-shrink-0" style={{ width: 120, height: 40 }}>
                        <img src={url} alt={`Sponsor ${i + 1}`} className="w-full h-full object-contain p-1" />
                        <button
                          type="button"
                          onClick={() => removeSponsorLogo(i)}
                          className="absolute inset-0 bg-red-500/80 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                          title="Remove"
                        >
                          <X size={16} className="text-white" />
                        </button>
                      </div>
                      <input
                        type="url"
                        value={formData.sponsor_links?.[i] || ''}
                        onChange={(e) => updateSponsorLink(i, e.target.value)}
                        placeholder="https://sponsor-website.com (optional)"
                        className="flex-1 px-3 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-kod-accent"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Tournament Rules
              </label>
              <div className="space-y-2">
                {formData.rules?.map((rule, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => {
                        const newRules = [...(formData.rules || [])];
                        newRules[index] = e.target.value;
                        setFormData({ ...formData, rules: newRules });
                      }}
                      placeholder={`Rule ${index + 1}`}
                      className="flex-1 px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newRules = formData.rules?.filter((_, i) => i !== index) || [];
                        setFormData({ ...formData, rules: newRules });
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Remove rule"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const newRules = [...(formData.rules || []), ''];
                    setFormData({ ...formData, rules: newRules });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg text-sm transition-colors"
                >
                  <Plus size={16} />
                  Add Rule
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">
                Songs ({formData.song_ids.length} selected)
              </label>
              <input
                type="text"
                placeholder="Search songs..."
                value={songSearch}
                onChange={(e) => setSongSearch(e.target.value)}
                className="w-full px-4 py-2 mb-3 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              />
              <div className="max-h-64 overflow-y-auto bg-kod-purple-900/50 rounded-lg p-2 space-y-1">
                {filteredSongs.map(song => (
                  <label
                    key={song.id}
                    className="flex items-center p-2 hover:bg-kod-purple-700/50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.song_ids.includes(song.id)}
                      onChange={() => toggleSong(song.id)}
                      className="mr-3"
                    />
                    <img
                      src={song.cover_url}
                      alt={song.title}
                      className="w-10 h-10 rounded object-cover mr-3"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{song.title}</div>
                      <div className="text-sm text-white/60 truncate">{song.artist}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </form>

        <div className="p-6 border-t border-kod-purple-600 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 btn-outline"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 btn-primary"
          >
            {saving ? 'Saving...' : tournament ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

interface InviteModalProps {
  tournament: Tournament;
  onClose: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ tournament, onClose }) => {
  const [users, setUsers] = useState<Array<{ id: string; username: string; full_name: string | null }>>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [bracketExists, setBracketExists] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<any | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, invitationsData, hasBracket] = await Promise.all([
        loadUsers(),
        tournamentService.getTournamentInvitations(tournament.id),
        tournamentService.hasBracketBeenGenerated(tournament.id)
      ]);
      setUsers(usersData);
      setInvitations(invitationsData);
      setBracketExists(hasBracket);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipant = async (invitation: any) => {
    try {
      setRemovingId(invitation.id);
      await tournamentService.removeAcceptedParticipant(
        invitation.id,
        tournament.id,
        invitation.user_id
      );
      setConfirmRemove(null);
      await loadData();
    } catch (err) {
      console.error('Error removing participant:', err);
    } finally {
      setRemovingId(null);
    }
  };

  const loadUsers = async () => {
    return await tournamentService.getAllProfiles();
  };

  const handleSendInvitations = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setSending(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      await tournamentService.sendInvitations(tournament.id, selectedUsers, user.id);
      setSelectedUsers([]);
      await loadData();
    } catch (err) {
      console.error('Error sending invitations:', err);
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const alreadyInvited = invitations.some(inv => inv.user_id === user.id);
    const matchesSearch = (user.username?.toLowerCase() ?? '').includes(searchQuery.toLowerCase()) ||
                          (user.full_name?.toLowerCase() ?? '').includes(searchQuery.toLowerCase());
    return !alreadyInvited && matchesSearch;
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-kod-purple-800 rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-kod-purple-600">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Manage Invitations - {tournament.title}</h3>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Send New Invitations</h4>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 mb-3 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
            />
            <div className="max-h-48 overflow-y-auto bg-kod-purple-900/50 rounded-lg p-2 space-y-1 mb-3">
              {loading ? (
                <div className="text-center py-4 text-white/50">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-4 text-white/50">No users available to invite</div>
              ) : (
                filteredUsers.map(user => (
                  <label
                    key={user.id}
                    className="flex items-center p-2 hover:bg-kod-purple-700/50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">{user.username}</div>
                      {user.full_name && (
                        <div className="text-sm text-white/60">{user.full_name}</div>
                      )}
                    </div>
                  </label>
                ))
              )}
            </div>
            {selectedUsers.length > 0 && (
              <button
                onClick={handleSendInvitations}
                disabled={sending}
                className="btn-primary w-full"
              >
                {sending ? 'Sending...' : `Send ${selectedUsers.length} Invitation${selectedUsers.length > 1 ? 's' : ''}`}
              </button>
            )}
          </div>

          <div>
            <h4 className="font-semibold mb-3">Existing Invitations ({invitations.length})</h4>
            <div className="space-y-2">
              {invitations.length === 0 ? (
                <div className="text-center py-4 text-white/50 bg-kod-purple-900/50 rounded-lg">
                  No invitations sent yet
                </div>
              ) : (
                invitations.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-kod-purple-900/50 rounded-lg">
                    <div>
                      <div className="font-medium">{(inv as any).profile?.username || 'Anonymous User'}</div>
                      <div className="text-sm text-white/60">
                        Invited {new Date(inv.invited_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold ${
                          inv.status === 'accepted'
                            ? 'bg-green-900/30 text-green-400'
                            : inv.status === 'declined'
                            ? 'bg-red-900/30 text-red-400'
                            : 'bg-yellow-900/30 text-yellow-400'
                        }`}
                      >
                        {inv.status}
                      </span>
                      {inv.status === 'accepted' && !bracketExists && (
                        <button
                          onClick={() => setConfirmRemove(inv)}
                          className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Remove participant"
                        >
                          <UserX size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-kod-purple-600">
          <button onClick={onClose} className="btn-outline w-full">
            Close
          </button>
        </div>
      </div>

      {confirmRemove && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-kod-purple-800 rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center mb-4">
              <div className="bg-red-500/10 p-3 rounded-full mr-4">
                <AlertTriangle className="text-red-500" size={24} />
              </div>
              <h3 className="text-lg font-bold">Remove Participant</h3>
            </div>
            <p className="text-white/70 mb-6">
              Remove <span className="text-white font-semibold">{confirmRemove.profile?.username}</span> from this tournament? Their invitation and entry will be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRemove(null)}
                className="btn-outline"
                disabled={removingId === confirmRemove.id}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveParticipant(confirmRemove)}
                disabled={removingId === confirmRemove.id}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md transition-colors flex items-center"
              >
                {removingId === confirmRemove.id ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Removing...
                  </div>
                ) : (
                  <>
                    <UserX size={16} className="mr-2" />
                    Remove
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ParticipantsModalProps {
  tournament: Tournament;
  onClose: () => void;
  onUpdate: () => void;
}

interface TournamentParticipant {
  id: string;
  user_id: string;
  performance_id: string | null;
  status: string;
  score: number | null;
  is_disqualified: boolean;
  finalist_placement: number | null;
  admin_notes: string | null;
  created_at: string;
  current_round: number | null;
  profile?: {
    username: string;
    full_name: string | null;
    avatar_url: string | null;
  };
}

interface ParticipantRecording {
  id: string;
  user_id: string;
  recording_type: string;
  recording_url: string;
  mixed_recording_url: string | null;
  final_video_url: string | null;
  is_tournament_submission: boolean;
  tournament_round: number | null;
  submitted_at: string | null;
  created_at: string;
  processing_status: string | null;
  score: number | null;
  song: {
    title: string;
    artist: string;
  } | null;
}

const ParticipantsModal: React.FC<ParticipantsModalProps> = ({ tournament, onClose, onUpdate }) => {
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [recordingsMap, setRecordingsMap] = useState<Record<string, ParticipantRecording[]>>({});
  const [expandedParticipants, setExpandedParticipants] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);
  const [showFinalistModal, setShowFinalistModal] = useState<string | null>(null);
  const [finalistPlacement, setFinalistPlacement] = useState<number>(1);
  const [showResetConfirm, setShowResetConfirm] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadParticipants();
  }, [tournament.id]);

  const toggleExpanded = (participantId: string) => {
    setExpandedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  const loadParticipants = async () => {
    try {
      setLoading(true);
      const [entriesData, recordingsData] = await Promise.all([
        fetchAllRows<any>(() =>
          supabase
            .from('tournament_entries')
            .select(`
              *,
              profile:profiles (username, full_name, avatar_url)
            `)
            .eq('tournament_id', tournament.id)
            .order('created_at', { ascending: false })
        ),
        fetchAllRows<any>(() =>
          supabase
            .from('recordings')
            .select(`
              id, user_id, recording_type, recording_url, mixed_recording_url,
              final_video_url, is_tournament_submission, tournament_round,
              submitted_at, created_at, processing_status,
              song:songs (title, artist)
            `)
            .eq('tournament_id', tournament.id)
            .order('tournament_round', { ascending: true })
            .order('created_at', { ascending: false })
        ),
      ]);

      setParticipants(entriesData);

      const rMap: Record<string, ParticipantRecording[]> = {};
      for (const rec of recordingsData as any[]) {
        if (!rMap[rec.user_id]) rMap[rec.user_id] = [];
        const perfResult = await supabase
          .from('performances')
          .select('id')
          .eq('recording_id', rec.id)
          .maybeSingle();

        let score: number | null = null;
        if (perfResult.data?.id) {
          const analysisResult = await supabase
            .from('performance_analysis')
            .select('overall_score')
            .eq('performance_id', perfResult.data.id)
            .maybeSingle();
          score = analysisResult.data?.overall_score ?? null;
        }

        rMap[rec.user_id].push({
          ...rec,
          score,
          song: rec.song
        });
      }
      setRecordingsMap(rMap);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError('Failed to load participants');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    try {
      setActionLoading(participantId);
      const { error } = await supabase
        .from('tournament_entries')
        .delete()
        .eq('id', participantId);

      if (error) throw error;

      setSuccess('Participant removed successfully');
      setShowRemoveConfirm(null);
      await loadParticipants();
      onUpdate();
    } catch (err) {
      console.error('Error removing participant:', err);
      setError('Failed to remove participant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleDisqualify = async (participantId: string, currentStatus: boolean) => {
    try {
      setActionLoading(participantId);
      const { error } = await supabase
        .from('tournament_entries')
        .update({ is_disqualified: !currentStatus })
        .eq('id', participantId);

      if (error) throw error;

      setSuccess(currentStatus ? 'Participant reinstated' : 'Participant disqualified');
      await loadParticipants();
    } catch (err) {
      console.error('Error toggling disqualification:', err);
      setError('Failed to update participant status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetRound = async (participantUserId: string) => {
    try {
      setActionLoading(participantUserId);
      setShowResetConfirm(null);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-participant-round`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            tournament_id: tournament.id,
            user_id: participantUserId,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset round');
      }

      setSuccess(`Round ${result.round_reset} reset — participant can re-record`);
      await loadParticipants();
      onUpdate();
    } catch (err) {
      console.error('Error resetting participant round:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset round');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetFinalist = async (participantId: string) => {
    try {
      setActionLoading(participantId);
      const { error } = await supabase
        .from('tournament_entries')
        .update({ finalist_placement: finalistPlacement })
        .eq('id', participantId);

      if (error) throw error;

      setSuccess(`Participant marked as ${getOrdinal(finalistPlacement)} place finalist`);
      setShowFinalistModal(null);
      setFinalistPlacement(1);
      await loadParticipants();
    } catch (err) {
      console.error('Error setting finalist:', err);
      setError('Failed to set finalist placement');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveFinalist = async (participantId: string) => {
    try {
      setActionLoading(participantId);
      const { error } = await supabase
        .from('tournament_entries')
        .update({ finalist_placement: null })
        .eq('id', participantId);

      if (error) throw error;

      setSuccess('Finalist placement removed');
      await loadParticipants();
    } catch (err) {
      console.error('Error removing finalist:', err);
      setError('Failed to remove finalist placement');
    } finally {
      setActionLoading(null);
    }
  };

  const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const filteredParticipants = participants.filter(p => {
    if (!searchQuery) return true;
    const username = p.profile?.username?.toLowerCase() || '';
    const fullName = p.profile?.full_name?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return username.includes(query) || fullName.includes(query);
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-kod-purple-800 rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-kod-purple-600">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Manage Participants - {tournament.title}</h3>
              <p className="text-white/60 text-sm mt-1">{participants.length} total participants</p>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-4 flex items-center">
              <AlertTriangle size={20} className="text-red-400 mr-3" />
              <p className="text-red-400">{error}</p>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
                <X size={18} />
              </button>
            </div>
          )}

          {success && (
            <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4 flex items-center">
              <Check size={20} className="text-green-400 mr-3" />
              <p className="text-green-400">{success}</p>
              <button onClick={() => setSuccess(null)} className="ml-auto text-green-400 hover:text-green-300">
                <X size={18} />
              </button>
            </div>
          )}

          <div className="mb-4">
            <input
              type="text"
              placeholder="Search participants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
            />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kod-accent mx-auto"></div>
            </div>
          ) : filteredParticipants.length === 0 ? (
            <div className="text-center py-12 text-white/50">
              {searchQuery ? 'No participants found matching your search' : 'No participants yet'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredParticipants.map((participant) => {
                const recordings = recordingsMap[participant.user_id] || [];
                const isExpanded = expandedParticipants.has(participant.id);
                const hasRecordings = recordings.length > 0;
                const bestScore = recordings.reduce((best, r) => {
                  if (r.score !== null && (best === null || r.score > best)) return r.score;
                  return best;
                }, null as number | null);
                const submittedCount = recordings.filter(r => r.is_tournament_submission).length;

                return (
                  <div
                    key={participant.id}
                    className={`rounded-lg overflow-hidden ${
                      participant.is_disqualified ? 'opacity-60' : ''
                    }`}
                  >
                    <div
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isExpanded
                          ? 'bg-kod-purple-700/80'
                          : 'bg-kod-purple-900/50 hover:bg-kod-purple-800/60'
                      } ${participant.is_disqualified ? 'border-l-2 border-red-500/50' : ''}`}
                      onClick={() => hasRecordings && toggleExpanded(participant.id)}
                    >
                      {hasRecordings ? (
                        isExpanded ? (
                          <ChevronDown size={16} className="text-white/50 shrink-0" />
                        ) : (
                          <ChevronRight size={16} className="text-white/50 shrink-0" />
                        )
                      ) : (
                        <div className="w-4 shrink-0" />
                      )}

                      <div className="flex-1 min-w-0 flex items-center gap-3">
                        <span className="font-medium text-sm truncate">
                          {participant.profile?.username || 'Anonymous User'}
                        </span>
                        {participant.profile?.full_name && (
                          <span className="text-xs text-white/40 truncate hidden sm:inline">
                            ({participant.profile.full_name})
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                          participant.status === 'submitted'
                            ? 'bg-green-900/30 text-green-400'
                            : participant.status === 'evaluated'
                            ? 'bg-blue-900/30 text-blue-400'
                            : 'bg-gray-900/30 text-gray-400'
                        }`}>
                          {participant.status}
                        </span>

                        {hasRecordings && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-kod-accent/20 text-kod-accent flex items-center gap-1">
                            <Mic size={10} />
                            {recordings.length}
                          </span>
                        )}

                        {bestScore !== null && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-900/30 text-amber-400 flex items-center gap-1">
                            <Star size={10} />
                            {Math.round(bestScore)}
                          </span>
                        )}

                        {participant.is_disqualified && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-900/30 text-red-400">
                            DQ
                          </span>
                        )}

                        {participant.finalist_placement && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-900/30 text-yellow-400 flex items-center gap-1">
                            <Award size={10} />
                            {getOrdinal(participant.finalist_placement)}
                          </span>
                        )}

                        <span className="text-[11px] text-white/30 hidden md:inline">
                          {new Date(participant.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => setShowResetConfirm(participant.user_id)}
                          disabled={actionLoading === participant.user_id || !hasRecordings}
                          className="p-1.5 rounded text-white/40 hover:text-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Reset Current Round"
                        >
                          {actionLoading === participant.user_id ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b border-current" />
                          ) : (
                            <RotateCcw size={14} />
                          )}
                        </button>

                        <button
                          onClick={() => handleToggleDisqualify(participant.id, participant.is_disqualified)}
                          disabled={actionLoading === participant.id}
                          className={`p-1.5 rounded transition-colors ${
                            participant.is_disqualified
                              ? 'text-green-400 hover:bg-green-500/10'
                              : 'text-white/40 hover:text-red-400 hover:bg-red-500/10'
                          }`}
                          title={participant.is_disqualified ? 'Reinstate' : 'Disqualify'}
                        >
                          {actionLoading === participant.id ? (
                            <div className="animate-spin rounded-full h-3.5 w-3.5 border-b border-current" />
                          ) : (
                            <Ban size={14} />
                          )}
                        </button>

                        {participant.finalist_placement ? (
                          <button
                            onClick={() => handleRemoveFinalist(participant.id)}
                            disabled={actionLoading === participant.id}
                            className="p-1.5 rounded text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                            title="Remove Finalist"
                          >
                            <Award size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setShowFinalistModal(participant.id);
                              setFinalistPlacement(1);
                            }}
                            disabled={actionLoading === participant.id}
                            className="p-1.5 rounded text-white/40 hover:text-yellow-400 hover:bg-yellow-500/10 transition-colors"
                            title="Set Finalist"
                          >
                            <Award size={14} />
                          </button>
                        )}

                        {!hasRecordings && (
                          <button
                            onClick={() => setShowRemoveConfirm(participant.id)}
                            disabled={actionLoading === participant.id}
                            className="p-1.5 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Remove"
                          >
                            <UserX size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {isExpanded && hasRecordings && (
                      <div className="bg-kod-purple-900/30 border-t border-kod-purple-700/50">
                        <div className="px-4 py-2">
                          <div className="text-[11px] text-white/40 uppercase tracking-wider mb-2 pl-7">
                            {recordings.length} Recording{recordings.length !== 1 ? 's' : ''}
                            {submittedCount > 0 && ` / ${submittedCount} Submitted`}
                          </div>
                          <div className="space-y-1">
                            {recordings.map((rec) => (
                              <div
                                key={rec.id}
                                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                                  rec.is_tournament_submission
                                    ? 'bg-kod-accent/5 border border-kod-accent/20'
                                    : 'bg-kod-purple-800/30'
                                }`}
                              >
                                <div className="shrink-0">
                                  {rec.recording_type === 'video' ? (
                                    <Video size={14} className="text-blue-400" />
                                  ) : (
                                    <Mic size={14} className="text-green-400" />
                                  )}
                                </div>

                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                  {rec.tournament_round !== null && (
                                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-white/10 text-white/60 shrink-0">
                                      R{rec.tournament_round}
                                    </span>
                                  )}
                                  {rec.is_tournament_submission && (
                                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-kod-accent/20 text-kod-accent shrink-0">
                                      Submitted
                                    </span>
                                  )}
                                  {rec.song && (
                                    <span className="text-xs text-white/50 truncate">
                                      {rec.song.title} - {rec.song.artist}
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  {rec.score !== null && (
                                    <span className="text-xs text-amber-400 font-medium">
                                      {Math.round(rec.score)}
                                    </span>
                                  )}
                                  {rec.processing_status && rec.processing_status !== 'completed' && (
                                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${
                                      rec.processing_status === 'error'
                                        ? 'bg-red-900/30 text-red-400'
                                        : 'bg-yellow-900/30 text-yellow-400'
                                    }`}>
                                      {rec.processing_status}
                                    </span>
                                  )}
                                  <span className="text-[11px] text-white/30">
                                    {new Date(rec.created_at).toLocaleDateString()}
                                  </span>
                                  <a
                                    href={rec.final_video_url || rec.mixed_recording_url || rec.recording_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 rounded text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                                    title="Open Recording"
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-kod-purple-600">
          <button onClick={onClose} className="btn-outline w-full">
            Close
          </button>
        </div>

        {showRemoveConfirm && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-lg">
            <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md">
              <div className="flex items-center mb-4">
                <div className="bg-red-500/20 p-3 rounded-full mr-4">
                  <AlertTriangle size={24} className="text-red-400" />
                </div>
                <h3 className="text-xl font-bold">Remove Participant</h3>
              </div>
              <p className="text-white/80 mb-6">
                Are you sure you want to remove this participant? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRemoveConfirm(null)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRemoveParticipant(showRemoveConfirm)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}

        {showResetConfirm && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-lg">
            <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md">
              <div className="flex items-center mb-4">
                <div className="bg-amber-500/20 p-3 rounded-full mr-4">
                  <RotateCcw size={24} className="text-amber-400" />
                </div>
                <h3 className="text-xl font-bold">Reset Current Round</h3>
              </div>
              <p className="text-white/80 mb-6">
                This will delete this participant's most recent round recording, score, and performance data. Their match will be reopened so they can re-record.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(null)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResetRound(showResetConfirm)}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Reset Round
                </button>
              </div>
            </div>
          </div>
        )}

        {showFinalistModal && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-lg">
            <div className="bg-kod-purple-800 rounded-lg p-6 max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Set Finalist Placement</h3>
                <button onClick={() => setShowFinalistModal(null)} className="text-white/70 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <p className="text-white/80 mb-4">
                Select the finalist placement (1st through 10th):
              </p>
              <select
                value={finalistPlacement}
                onChange={(e) => setFinalistPlacement(Number(e.target.value))}
                className="w-full px-4 py-2 mb-6 bg-kod-purple-700 border border-kod-purple-600 rounded-lg text-white focus:outline-none focus:border-kod-accent"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <option key={n} value={n}>{getOrdinal(n)} Place</option>
                ))}
              </select>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFinalistModal(null)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSetFinalist(showFinalistModal)}
                  disabled={actionLoading === showFinalistModal}
                  className="flex-1 btn-primary"
                >
                  {actionLoading === showFinalistModal ? 'Setting...' : 'Set Finalist'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
